import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { defaultOptions, renderAsync } from 'docx-preview';
import { useState, useEffect } from 'react';
import { styled, css } from 'styled-components';
import { useResourceDocumentDataContextSafe } from '../../../context/resourceDataContext.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useQueryDocumentResourceFileContent } from '../../../../queries/useQueryDocumentResourceFileContent.js';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { isString } from '@tiptap/core';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { useSelectionBasedAnnotations } from '../../../../../annotation/hooks/useSelectionBasedAnnotations.js';
import { TextToolbar } from './TextToolbar.js';

const DocxFlex = styled(Flex).withConfig({
  displayName: "ResourceDocx__DocxFlex",
  componentId: "sc-b3c030b3-0"
})([
  `--scale:1;--translate-x:0;transform:translateX(var(--translate-x)) scale(var(--scale));transform-origin:top left;padding-bottom:100px;`,
  ``
], ({ docxClassName }) => css([
  `.`,
  `{table{overflow-x:auto;max-width:100%;}.`,
  `-comment-ref:hover ~ .`,
  `-comment-popover{display:grid;}.`,
  `_commentreference{position:relative;padding:0.2rem;}.`,
  `-comment-popover{top:calc(100% + 1rem);left:0;transform:translateX(-50%);padding:0.8rem;gap:0.5rem;grid-template-columns:auto 1fr;grid-template-areas:'author date' 'content content';text-indent:0;background-color:`,
  `;border-radius:0.5rem;align-items:center;width:auto;.`,
  `-comment-author{white-space:nowrap;grid-area:author;font-weight:bold;color:`,
  `;}.`,
  `-comment-date{white-space:nowrap;grid-area:date;font-size:0.8rem;color:`,
  `;justify-self:end;}.`,
  `_commenttext{.`,
  `_commentreference{display:none;}color:`,
  `;grid-area:content;text-indent:0;display:flex;flex-direction:column;gap:0.5rem;text-align:left;> span::before{content:'•';margin-right:0.5rem;}}}}`
], docxClassName, docxClassName, docxClassName, docxClassName, docxClassName, cssVar["color-bg-primary"], docxClassName, cssVar["color-text-primary"], docxClassName, cssVar["color-text-tertiary"], docxClassName, docxClassName, cssVar["color-text-primary"]));
const Wrapper = styled.div.withConfig({
  displayName: "ResourceDocx__Wrapper",
  componentId: "sc-b3c030b3-1"
})([
  `flex-grow:1;display:flex;flex-direction:column;`
]);
const docxOptions = {
  ...defaultOptions,
  renderComments: false,
  inWrapper: false,
  className: "docx"
};
function ptToPxDynamic(pt) {
  return pt * (96 / 72);
}
const ResourceDocx = () => {
  const { resource } = useResourceDocumentDataContextSafe();
  const { data: fileContent } = useQueryDocumentResourceFileContent(resource);
  const [targetRef, setTargetRef] = useState(null);
  const [wrapperEl, setWrapperEl] = useState(null);
  const [scrollAreaEl, setScrollAreaEl] = useState(null);
  const [scrollAreaRect] = useDomContentRect(scrollAreaEl);
  const [pageWidthPx, setPageWidthPx] = useState(null);
  const [pageMargins, setPageMargins] = useState(null);
  const [uniformMargins, setUniformMargins] = useState(false);
  const [documentBgColor, setDocumentBgColor] = useState(void 0);
  const [rendered, setRendered] = useState(false);
  const { onAnnotationCreation, onHighlightCreation } = useSelectionBasedAnnotations({
    doc: targetRef,
    enabled: rendered,
    resourceId: resource.id
  });
  const className = `docx-${resource.id}`;
  useEffect(() => {
    if (!fileContent || !targetRef) return;
    renderAsync(fileContent, targetRef, void 0, {
      ...docxOptions,
      className
    }).then((parsedDocument) => {
      const pageWidthPt = Number(parsedDocument.documentPart.body.props.pageSize.width.slice(0, -2));
      const pageWidthPx2 = ptToPxDynamic(pageWidthPt);
      let pageMarginsPt = parsedDocument.documentPart.body.props.pageMargins;
      let uniformMargins2 = true;
      let hasSectionProps = false;
      const body = parsedDocument.documentPart.body;
      for (let i = 0; i < body.children.length; i++) {
        const element = body.children[i];
        if ("sectionProps" in element) {
          if (!hasSectionProps) {
            pageMarginsPt = element.sectionProps.pageMargins;
            hasSectionProps = true;
            continue;
          }
          const sectionPageMargins = element.sectionProps.pageMargins;
          for (const [key, value] of Object.entries(sectionPageMargins)) {
            if (pageMarginsPt[key] !== value) {
              uniformMargins2 = false;
              break;
            }
          }
        }
      }
      const pageMargins2 = Object.entries(pageMarginsPt).reduce((acc, [key, value]) => {
        acc[key] = isString(value) ? ptToPxDynamic(Number(value.slice(0, -2))) : 0;
        return acc;
      }, {});
      setPageWidthPx(pageWidthPx2);
      setPageMargins(pageMargins2);
      setUniformMargins(uniformMargins2);
      setDocumentBgColor(parsedDocument.documentPart.body.cssStyle?.["background-color"] ?? "white");
      setRendered(true);
    });
    return () => {
      setRendered(false);
    };
  }, [
    className,
    fileContent,
    targetRef
  ]);
  const [scaled, setScaled] = useState(false);
  useClientLayoutEffect(() => {
    if (!pageWidthPx || !scrollAreaRect || !pageMargins || !targetRef || !wrapperEl) return;
    const maxPageWidth = scrollAreaRect.width;
    let scale = maxPageWidth / pageWidthPx;
    const minPadding = 64;
    const marginWidth = pageMargins.left + pageMargins.right;
    if (scale < 0.8 && marginWidth > minPadding && uniformMargins) {
      scale = maxPageWidth / (pageWidthPx - marginWidth + minPadding);
      targetRef.style.setProperty("--translate-x", `${-((marginWidth - minPadding) / 2) * scale}px`);
    } else {
      targetRef.style.setProperty("--translate-x", "0");
    }
    targetRef.style.setProperty("--scale", scale.toString());
    const targetRect = targetRef.getBoundingClientRect();
    wrapperEl.style.height = `${targetRect.height}px`;
    setScaled(true);
  }, [
    pageWidthPx,
    scrollAreaRect,
    uniformMargins
  ]);
  const handleOnClick = (e) => {
    e.preventDefault();
    if (!(e.target instanceof HTMLElement)) return;
    const anchor = e.target.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href) return;
    if (!href?.startsWith("#")) {
      window.open(anchor.href, "_blank");
      return;
    }
    const heading = e.currentTarget.querySelector(href);
    if (heading) {
      heading.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx(ScrollArea.Default, {
        "data-pan-id-ignore-resource-drag-resize": "xy",
        ref: setScrollAreaEl,
        orientation: "vertical",
        asFlex: true,
        grow: true,
        expandVertically: true,
        children: /* @__PURE__ */ jsx(Wrapper, {
          ref: setWrapperEl,
          style: {
            // we add bg color here as well to try and avoid flickering when scrolling fast
            backgroundColor: documentBgColor
          },
          children: /* @__PURE__ */ jsx(DocxFlex, {
            docxClassName: className,
            onClick: handleOnClick,
            direction: "column",
            minWidth: "100%",
            ref: setTargetRef,
            style: {
              visibility: scaled ? "visible" : "hidden",
              backgroundColor: documentBgColor
            }
          })
        })
      }),
      /* @__PURE__ */ jsx(TextToolbar, {
        documentEl: targetRef,
        onAnnotationCreation,
        onHighlightCreation
      })
    ]
  });
};

export { ResourceDocx };
//# sourceMappingURL=ResourceDocx.js.map
